// MapComponent.tsx
import React, { useEffect, useState } from 'react'
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet'
import L, { LatLngExpression } from 'leaflet'
import 'leaflet/dist/leaflet.css'
import { CurrentSession } from '@/services/repository/session'
import { Services } from '@/services/services'
import { useNavigate } from 'react-router-dom'
import { useModal } from '../modal/Modal'
import { SuperAdminInsertMachinesPopup } from '@/pages/dashboard/superadmin/machines/Machines'
import { Roles } from '@/services/repository/users'
import { defaultMapCenter } from '@/env/config'
import { formatDate } from '@/common/time'

import './BatteryMap.css'

interface MapRowMachine {
  id: string | undefined
  name: string | undefined
  internalNumber: string | undefined
}

interface MapRow {
  imei: string
  time: string // ISO 8601 string representation of DtServer (e.g., "2025-01-17T12:34:56Z")
  dtTracker: string // ISO 8601 string representation of DtTracker (e.g., "2025-01-17T12:34:56Z")
  lat: number  // Latitude as a floating-point number
  lng: number  // Longitude as a floating-point number
  altitude: number // Altitude in meters
  angle: number // Angle in degrees
  speed: number // Speed in meters per second
  soc: number // SOC
  machine: MapRowMachine
}

interface MapResponse {
  time: string // ISO 8601 string representation of the latest DtServer
  rows: MapRow[] | null   // Array of MapRow objects
}

function getBatteryClass(percent: number): string {
  if (percent >= 80 && percent <= 100) {
    return "map-battery-green"; // 100–80%
  } else if (percent >= 60 && percent < 80) {
    return "map-battery-yellow"; // 80–60%
  } else if (percent >= 45 && percent < 60) {
    return "map-battery-orange"; // 60–45%
  } else {
    return "map-battery-red"; // 45–0%
  }
}

function mergeMapResponses(oldResponse: MapResponse, newResponse: MapResponse): MapResponse {
  if (!newResponse.rows) {
    return oldResponse
  }
  const updatedRows: MapRow[] = oldResponse.rows ? [...oldResponse.rows] : []
  const imeiMap = new Map<string, MapRow>(updatedRows.map(row => [row.imei, row]))
  for (const newRow of newResponse.rows) {
    imeiMap.set(newRow.imei, newRow)
  }
  const mergedRows = Array.from(imeiMap.values());
  return {
    time: newResponse.time,
    rows: mergedRows,
  }
}


export const BatteryMapComponent = ({ services, session, fetchUrl }: {
  services: Services
  session: CurrentSession
  fetchUrl: string
}) => {
  const [map, setMap] = useState<MapResponse | undefined>(undefined)
  const [myposition, setMyPosition] = useState<[number, number] | undefined>(undefined)
  const navigate = useNavigate()
  const { openModal } = useModal()

  const now = Date.now()
  const activeTime = 5 * 60 * 1000; // fiveMinutesInMs

  function isActive(dateStr: string): boolean {
    const inputDate = new Date(dateStr).getTime();
    if (isNaN(inputDate)) {
      throw new Error("Invalid date string");
    }
    return now - inputDate <= activeTime && inputDate <= now;
  }

  async function refreshMap(fromDate?: Date, oldResponse?: MapResponse): Promise<MapResponse> {
    return services.session.fetchWithToken(`${fetchUrl}?fromDate=${fromDate ? fromDate.toISOString() : ''}`).then(async (r) => {
      if (r.status !== 200) {
        const erros = await r.json()
        console.error(erros)
        return Promise.reject<MapResponse>()
      }
      const newResponse: MapResponse = await r.json()
      newResponse.rows = newResponse.rows || null
      if (oldResponse === undefined) {
        return newResponse
      }
      return mergeMapResponses(oldResponse, newResponse)
    })
  }

  useEffect(() => {
    // Function to handle geolocation success
    const handleSuccess = (location: GeolocationPosition) => {
      const { latitude, longitude } = location.coords
      setMyPosition([latitude, longitude])
    }

    // Function to handle geolocation error
    const handleError = (error: GeolocationPositionError) => {
      console.error('Error obtaining location:', error)
      // Fallback to a default position if geolocation fails
      setMyPosition(undefined)
    }

    // Check if geolocation is available
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(handleSuccess, handleError)
    } else {
      console.error('Geolocation is not supported by this browser.')
      // Fallback to a default position if geolocation is not supported
      setMyPosition(undefined)
    }
  }, [])

  useEffect(() => {
    refreshMap().then(setMap)
  }, []);

  useEffect(() => {
    if (map === undefined) {
      return
    }
    const clock = async () => {
      if (map === undefined) {
        return
      }
      await refreshMap(map?.time ? new Date(map?.time) : undefined, map).then(setMap)
      handle = setTimeout(clock, 2000)
    }
    let handle: NodeJS.Timeout = setTimeout(clock, 2000)
    return () => {
      clearTimeout(handle)
    }
  }, [map])

  if (map === undefined) {
    return <div className='spinner'></div>
  }

  function findLatestRow(response: MapResponse | undefined): MapRow | undefined {
    if (!response?.rows || response.rows.length === 0) {
      return undefined;
    }
  
    return response.rows.reduce((latest, current) => {
      return new Date(current.time) > new Date(latest.time) ? current : latest;
    });
  }

  const firstRow = findLatestRow(map);

  const center: LatLngExpression = (firstRow && {
    lat: firstRow.lat,
    lng: firstRow.lng
  }) || myposition || defaultMapCenter

  return (
    <MapContainer attributionControl={false} center={center} zoom={13} style={{ height: '100%', width: '100%' }}>
      <TileLayer
        attribution='evoltag.pl'
        url='https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
      />
      {map.rows?.map((row) => !row.soc ? null : (
        <Marker
          key={row.imei}
          position={row}
          icon={L.divIcon({
            className: ``,
            html: `<div class="map-point-battery ${getBatteryClass(row.soc)}"><div class="map-point-battery-title">${row.machine.internalNumber ? row.machine.internalNumber : 'IMEI: '+row.imei}</div><div class="map-point-battery-logo"><i class="svgfont-battery" /></div></div>`,
            // iconSize: [32, 32], // Adjust size as needed
            // iconAnchor: [16, 16], // Anchor point
          })} >
          <Popup>
            <div onClick={() => row.machine.id && navigate(`/communal/machine/${row.machine.id}`)}>

              {/* <a className='main-btn' onClick={}></a> */}

              {!row.machine.id ? <p style={{ cursor: 'pointer' }} onClick={() => session.user.role === Roles.SuperAdmin && openModal((props) => SuperAdminInsertMachinesPopup({
                ...props, services, session, defaultValues: {
                  imei: row.imei,
                }, onInsert: ({ id }) => {
                  navigate(`/communal/machine/${id}`)
                }
              }))}>{`IMEI: ${row.imei}`}</p> : null}
              {row.machine.id ? <p style={{ cursor: 'pointer' }}>{row.machine.name}</p> : null}
              {row.machine.internalNumber ? <p>{session.translates.for('map.internal_number')}: {row.machine.internalNumber}</p> : null }
              <p>{session.translates.for('map.soc')}: {row.soc}%</p>
              <p className='map-point-dttracker'>{formatDate(new Date(row.dtTracker))}</p>
              {session.user.role === Roles.SuperAdmin && !row.machine.id ? <p className='main-btn' onClick={() => openModal((props) => SuperAdminInsertMachinesPopup({
                ...props, services, session, defaultValues: {
                  imei: row.imei,
                }, onInsert: ({ id }) => {
                  navigate(`/communal/machine/${id}`)
                }
              }))}>{session.translates.for('map.add_btn')}</p> : null}
              
              {row.machine.id ? <p className='main-btn' onClick={() => navigate(`/communal/machine/${row.machine.id}`)}>{session.translates.for('map.machine_page')}</p> : null}
            </div>
          </Popup>
        </Marker>
      ))}

      {myposition ? <Marker
        key='my-location'
        position={myposition} 
        icon={L.divIcon({
          className: 'car-icon',
          html: `<img src="/public/img/gps/i_am_here.png" style="width: 32px; margin: -16px auto auto 0px" />`,
          iconSize: [32, 32], // Adjust size as needed
          iconAnchor: [16, 16], // Anchor point
        })}></Marker> : null}
    </MapContainer>
  )
}

export default BatteryMapComponent
